import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import LandingRouter from "@/pages/LandingRouter";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import ReportWizard from "@/pages/ReportWizard";
import ReportDetail from "@/pages/ReportDetail";
import PublicReport from "@/pages/PublicReport";
import PublicReportEdit from "@/pages/PublicReportEdit";
import AuthCallback from "@/pages/AuthCallback";
import NoticeWizard from "@/pages/NoticeWizard";
import NoticeView from "@/pages/NoticeView";
import PublicNotice from "@/pages/PublicNotice";
import CompanySettings from "@/pages/CompanySettings";
import Properties from "@/pages/Properties";
import QuickReport from "@/pages/QuickReport";
import PublicQuickReport from "@/pages/PublicQuickReport";
import NicheLanding from "@/pages/NicheLanding";
import Templates from "@/pages/Templates";
import MobileReportingLauncher from "@/pages/MobileReportingLauncher";
import FieldHome from "@/pages/FieldHome";
import InviteAccept from "@/pages/InviteAccept";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { NICHE_PAGES } from "@/data/nichePages";

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback synchronously during render (prevents race condition)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingRouter />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/r/:shareToken" element={<PublicReport />} />
      <Route path="/r-edit/:editToken" element={<PublicReportEdit />} />
      <Route path="/r-edit/:editToken/unit/:unitId" element={<PublicReportEdit />} />
      <Route path="/r-edit/:editToken/unit/:unitId/:section" element={<PublicReportEdit />} />
      <Route path="/q/:shareToken" element={<PublicQuickReport />} />
      <Route path="/t/:shareToken" element={<PublicNotice />} />
      <Route path="/kutsu/:token" element={<InviteAccept />} />
      <Route path="/unohdin-salasanan" element={<ForgotPassword />} />
      <Route path="/palauta-salasana/:token" element={<ResetPassword />} />
      <Route
        path="/kentta"
        element={
          <ProtectedRoute chromeless>
            <FieldHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app"
        element={
          <ProtectedRoute ownerOnly>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/uusi"
        element={
          <ProtectedRoute ownerOnly>
            <ReportWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/raportti/:reportId"
        element={
          <ProtectedRoute ownerOnly>
            <ReportDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/raportti/:reportId/kentta"
        element={
          <ProtectedRoute ownerOnly>
            <MobileReportingLauncher />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/asukastiedote/uusi"
        element={
          <ProtectedRoute ownerOnly>
            <NoticeWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/asukastiedote/:noticeId/muokkaa"
        element={
          <ProtectedRoute ownerOnly>
            <NoticeWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/asukastiedote/:noticeId"
        element={
          <ProtectedRoute ownerOnly>
            <NoticeView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/asetukset"
        element={
          <ProtectedRoute ownerOnly>
            <CompanySettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/kohteet"
        element={
          <ProtectedRoute ownerOnly>
            <Properties />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/kohteet/:groupId"
        element={
          <ProtectedRoute ownerOnly>
            <Properties />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/kohteet/:groupId/:propertyId"
        element={
          <ProtectedRoute ownerOnly>
            <Properties />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/tyopohjat"
        element={
          <ProtectedRoute ownerOnly>
            <Templates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/pikaraportti/uusi"
        element={
          <ProtectedRoute ownerOnly>
            <QuickReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/pikaraportti/:id"
        element={
          <ProtectedRoute ownerOnly>
            <QuickReport />
          </ProtectedRoute>
        }
      />
      {/* SEO niche landing pages — one route per slug, all share NicheLanding.jsx */}
      {NICHE_PAGES.map((p) => (
        <Route key={p.slug} path={`/${p.slug}`} element={<NicheLanding />} />
      ))}
      {/* Wildcard: mikä tahansa tuntematon URL ohjautuu etusivulle jotta
          käyttäjä ei jää tyhjän valkoisen ruudun eteen (esim. jos sähköpostin
          kutsulinkki avataan ympäristössä jossa reittiä ei ole vielä). */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster richColors position="top-center" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
